import React, { useEffect, useMemo } from 'react';
import { Button, Select, Form, Typography, Alert, Spin, Switch } from 'antd';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  selectOperator,
  setOperatorInjured,
  setOperatorKnown,
  fetchOperators,
  computeOperatorAvailability,
} from '../../../redux/reducers/operatorInfoSlice';
import type { Operator, VehicleOperator } from '../../../types/api.types';

const { Title } = Typography;
const { Option } = Select;

interface OperatorSelectionStepProps {
  onNext: () => void;
  onPrevious: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

const OperatorSelectionStep: React.FC<OperatorSelectionStepProps> = ({ 
  onNext, 
  onPrevious 
}) => {
  const dispatch = useAppDispatch();
  const auth = useAppSelector((state) => state.auth);
  const operatorInfo = useAppSelector((state) => state.operatorInfo);
  const vehicle = useAppSelector((state) => state.vehicle);
  const [form] = Form.useForm();

  const userId = auth.userData?.id ?? '';
  const vehicleOperators: VehicleOperator[] = vehicle.vehicle?.vehicleOperators ?? [];

  const allAvailableOperators = useMemo((): Operator[] => {
    const vehicleOpIds = vehicleOperators.map(vo => vo.operatorId);
    return operatorInfo.operators.filter(
      (o: Operator) => vehicleOpIds.includes(o.id) || o.id === userId
    );
  }, [operatorInfo.operators, vehicleOperators, userId]);

  useEffect(() => {
    if (operatorInfo.operators.length === 0) {
      dispatch(fetchOperators());
    }
  }, []);

  useEffect(() => {
    if (operatorInfo.operators.length > 0 && userId) {
      dispatch(computeOperatorAvailability({ userId, vehicleOperators }));
    }
  }, [operatorInfo.operators, userId, vehicleOperators]);

  const handleOperatorKnownChange = (checked: boolean) => {
    dispatch(setOperatorKnown(checked));
    if (!checked) {
      form.setFieldsValue({ operatorId: undefined });
      dispatch(selectOperator(null));
    }
  };

  const handleOperatorChange = (operatorId: string) => {
    const selected = allAvailableOperators.find((op: Operator) => op.id === operatorId);
    if (selected) {
      dispatch(selectOperator(selected));
    }
  };

  const handleInjuredChange = (checked: boolean) => {
    form.setFieldsValue({ isInjured: checked });
    dispatch(setOperatorInjured(checked));
  };

  const handleSubmit = () => {
    if (!operatorInfo.operatorKnown) {
      dispatch(selectOperator(null));
    }
    onNext();
  };

  const renderOperatorOption = (operator: Operator) => {
    const employeeLabel = operator.employeeNumber ? ` (${operator.employeeNumber})` : '';
    return (
      <Option key={operator.id} value={operator.id}>
        <div>
          <div style={{ fontWeight: 500 }}>{`${operator.firstName} ${operator.lastName} ${employeeLabel}\n${operator.email}`}</div>
        </div>
      </Option>
    );
  };

  return (
    <div className="step-container">
      <Title level={5} style={{ textAlign: 'center' }}>Operator Information</Title>

      {operatorInfo.fetching ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <p style={{ marginTop: 16 }}>Loading operators...</p>
        </div>
      ) : operatorInfo.error ? (
        <Alert
          message="Error Loading Operators"
          description={operatorInfo.error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={() => dispatch(fetchOperators())}>
              Retry
            </Button>
          }
          style={{ marginTop: 20 }}
        />
      ) : (
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          operatorId: operatorInfo.selectedOperator?.id,
          isInjured: operatorInfo.operatorInjured ?? false,
        }}
        style={{ marginTop: 20 }}
      >
        {operatorInfo.canPickOperator && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderBottom: '1px solid #f0f0f0' }}>
            <span>Operator is Known?</span>
            <Switch
              checked={operatorInfo.operatorKnown}
              onChange={handleOperatorKnownChange}
            />
          </div>
        )}

        {operatorInfo.canPickOperator && operatorInfo.operatorKnown && (
          <Form.Item
            label="Select Operator"
            name="operatorId"
            rules={[{ required: true, message: 'Please select an operator' }]}
          >
            <Select
              size="large"
              placeholder="Select operator"
              showSearch
              onChange={handleOperatorChange}
              filterOption={(input, option) => {
                const text = option?.label;
                if (typeof text === 'string') {
                  return text.toLowerCase().includes(input.toLowerCase());
                }
                return false;
              }}
            >
              {allAvailableOperators.map(renderOperatorOption)}
            </Select>
          </Form.Item>
        )}

        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.isInjured !== cur.isInjured}>
          {({ getFieldValue }) => (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderBottom: '1px solid #f0f0f0' }}>
                <span>Operator Injured in Incident?</span>
                <Switch
                  checked={!!getFieldValue('isInjured')}
                  onChange={handleInjuredChange}
                />
              </div>
              {getFieldValue('isInjured') && (
                <Alert
                  message="Injury Reported"
                  description="Since an injury has been reported, additional documentation may be required. Please ensure all relevant details are captured in the incident details section."
                  type="warning"
                  showIcon
                  style={{ marginTop: 12, marginBottom: 20 }}
                />
              )}
            </>
          )}
        </Form.Item>

        <div className="step-actions" style={{ marginTop: 40 }}>
          <Button size="large" onClick={onPrevious} style={{ color: '#FF9902', fontWeight: 'bold' }}>
            PREVIOUS
          </Button>
          <Button 
            type="primary" 
            size="large"
            htmlType="submit"
            style={{ fontWeight: 'bold' }}
          >
            CONTINUE TO PHOTOS
          </Button>
        </div>
      </Form>
      )}
    </div>
  );
};

export default OperatorSelectionStep;